import React, { FC } from 'react';
import { Box, Slider, Typography } from '@mui/material';
import { ProgressStep } from '@monorepo/types';
import { UseProgressDebugReturn } from '../hooks/use-progress-debug.hook';

interface DebugPanelProps {
  steps: ProgressStep[];
  activeStepIndex: number;
  progressPercentage: number;
  currentUserProgress: number;
  hasError: boolean;
  debug: UseProgressDebugReturn;
  onDebugStepClick?: (stepId: string) => void;
  groupInfo?: { groupId: string; groupName: string };
}

export const DebugPanel: FC<DebugPanelProps> = ({
  steps,
  activeStepIndex,
  progressPercentage,
  currentUserProgress,
  hasError,
  debug,
  onDebugStepClick,
  groupInfo,
}) => {
  return (
    <Box
      sx={(theme) => ({
        mt: 2,
        p: 2,
        bgcolor: theme.palette.background.paper,
        borderRadius: 2,
        border: `2px solid ${theme.palette.primary.main}`,
        fontSize: '0.8rem',
        boxShadow: theme.shadows[4],
      })}
    >
      <Typography
        variant="h6"
        component="div"
        sx={(theme) => ({
          mb: 1.5,
          color: theme.palette.primary.main,
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '1rem',
        })}
      >
        <span role="img" aria-label="Tools">
          🔧
        </span>{' '}
        Debug Panel
      </Typography>

      {/* Group Information */}
      {groupInfo && (
        <Box
          sx={(theme) => ({
            mb: 2,
            p: 1.5,
            bgcolor: theme.palette.action.hover,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Typography
            variant="caption"
            component="div"
            sx={(theme) => ({
              fontWeight: 'bold',
              mb: 0.5,
              color: theme.palette.text.primary,
              fontSize: '0.85rem',
            })}
          >
            <span role="img" aria-label="Label">
              🏷️
            </span>{' '}
            Group Information:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography
              variant="caption"
              component="span"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontSize: '0.8rem',
              })}
            >
              ID: {groupInfo.groupId}
            </Typography>
            <Typography
              variant="caption"
              component="span"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontSize: '0.8rem',
              })}
            >
              Name: {groupInfo.groupName}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Debug PNL Slider */}
      <Box
        sx={(theme) => ({
          mb: 2,
          p: 1.5,
          bgcolor: theme.palette.action.selected,
          borderRadius: 2,
          border: `2px solid ${theme.palette.primary.main}`,
          boxShadow: theme.shadows[2],
        })}
      >
        <Typography
          variant="caption"
          component="div"
          sx={(theme) => ({
            mb: 1,
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            fontSize: '0.85rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          })}
        >
          <span role="img" aria-label="Money">
            💰
          </span>{' '}
          PNL Simulator
          <Box
            sx={(theme) => ({
              bgcolor:
                debug.debugPNL >= 0
                  ? theme.palette.success.main
                  : theme.palette.error.main,
              color: 'white',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.8rem',
              fontWeight: 'bold',
            })}
          >
            ${debug.debugPNL.toLocaleString()}
          </Box>
        </Typography>
        {activeStepIndex >= 0 && (
          <>
            <Slider
              value={debug.debugPNL}
              onChange={(_, value) => debug.setDebugPNL(value as number)}
              min={-steps[activeStepIndex].target * 0.5} // Allow some negative PNL
              max={steps[activeStepIndex].target * 1.5} // Allow exceeding target
              step={100}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `$${value.toLocaleString()}`}
              sx={{ width: '100%' }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 1,
              }}
            >
              <Typography variant="caption">
                -${(steps[activeStepIndex].target * 0.5).toLocaleString()}
              </Typography>
              <Typography variant="caption">
                ${(steps[activeStepIndex].target * 1.5).toLocaleString()}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Two Column Layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {/* Left Column */}
        <Box>
          {/* Basic Debug Info */}
          <Box
            sx={(theme) => ({
              mb: 2,
              p: 1.5,
              bgcolor: theme.palette.action.hover,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Typography
              variant="caption"
              component="div"
              sx={(theme) => ({
                fontWeight: 'bold',
                mb: 0.5,
                color: theme.palette.text.primary,
                fontSize: '0.85rem',
              })}
            >
              <span role="img" aria-label="Clipboard">
                📋
              </span>{' '}
              Basic Info:
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 0.5,
                mb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                })}
              >
                Original: ${currentUserProgress.toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                })}
              >
                Debug: ${debug.actualPNL.toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                })}
              >
                Progress: {progressPercentage.toFixed(1)}%
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                })}
              >
                Step: {activeStepIndex}/{steps.length}
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                })}
              >
                Error: {hasError ? 'Yes' : 'No'}
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                })}
              >
                Debug Error: {debug.actualError ? 'Yes' : 'No'}
              </Typography>
            </Box>
          </Box>

          {/* Debug Error Toggle */}
          <Box
            sx={(theme) => ({
              mb: 2,
              p: 1.5,
              bgcolor: theme.palette.action.hover,
              borderRadius: 2,
              border: `2px solid ${debug.debugError ? theme.palette.error.main : theme.palette.success.main}`,
              boxShadow: theme.shadows[1],
            })}
          >
            <Typography
              variant="caption"
              component="div"
              sx={(theme) => ({
                mb: 1,
                fontWeight: 'bold',
                color: debug.debugError
                  ? theme.palette.error.main
                  : theme.palette.success.main,
                fontSize: '0.85rem',
              })}
            >
              {debug.debugError ? '❌ Error Mode' : '✅ Normal Mode'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                })}
              >
                Toggle state:
              </Typography>
              <Box
                component="button"
                onClick={() => debug.setDebugError(!debug.debugError)}
                sx={(theme) => ({
                  padding: '4px 8px',
                  border: '2px solid',
                  borderColor: debug.debugError
                    ? theme.palette.error.main
                    : theme.palette.success.main,
                  borderRadius: 2,
                  backgroundColor: debug.debugError
                    ? theme.palette.error.main
                    : theme.palette.success.main,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: `0 4px 8px ${debug.debugError ? theme.palette.error.main : theme.palette.success.main}40`,
                  },
                })}
              >
                {debug.debugError ? (
                  <>
                    <span role="img" aria-label="Refresh">
                      🔄
                    </span>{' '}
                    Switch to Normal
                  </>
                ) : (
                  <>
                    <span role="img" aria-label="Warning">
                      ⚠️
                    </span>{' '}
                    Simulate Error
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* Progress Bar Visual Debug */}
          <Box
            sx={(theme) => ({
              mb: 2,
              p: 1.5,
              bgcolor: theme.palette.action.selected,
              borderRadius: 2,
              border: `1px solid ${theme.palette.warning.main}`,
              boxShadow: theme.shadows[1],
            })}
          >
            <Typography
              variant="caption"
              component="div"
              sx={(theme) => ({
                mb: 1,
                fontWeight: 'bold',
                color: theme.palette.warning.main,
                fontSize: '0.85rem',
              })}
            >
              <span role="img" aria-label="Chart">
                📊
              </span>{' '}
              Progress Bar Status
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={(theme) => ({ color: theme.palette.text.secondary })}
                >
                  Position:
                </Typography>
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    color:
                      activeStepIndex === 0
                        ? theme.palette.success.main
                        : theme.palette.info.main,
                    fontWeight: 'bold',
                  })}
                >
                  {activeStepIndex === 0
                    ? 'Starting Point'
                    : `After ${activeStepIndex} step${activeStepIndex > 1 ? 's' : ''}`}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={(theme) => ({ color: theme.palette.text.secondary })}
                >
                  Progress in Step:
                </Typography>
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    color:
                      progressPercentage > 50
                        ? theme.palette.success.main
                        : progressPercentage > 0
                          ? theme.palette.warning.main
                          : theme.palette.text.primary,
                    fontWeight: 'bold',
                  })}
                >
                  {progressPercentage.toFixed(1)}% completed
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={(theme) => ({ color: theme.palette.text.secondary })}
                >
                  Visual Width:
                </Typography>
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                    fontWeight: 'bold',
                  })}
                >
                  {(
                    (progressPercentage / 100) *
                    (100 / (steps.length - 1))
                  ).toFixed(1)}
                  % of line
                </Typography>
              </Box>

              {/* Core Values Debug */}
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: 'primary.main',
                  borderRadius: 1,
                  color: 'primary.contrastText',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 'bold',
                    display: 'block',
                    mb: 0.5,
                    color: 'inherit',
                  }}
                >
                  <span role="img" aria-label="Target">
                    🎯
                  </span>{' '}
                  Core Values:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.8rem',
                    display: 'block',
                    color: 'inherit',
                  }}
                >
                  stepCount: {steps.length} | activeStepIndex: {activeStepIndex}{' '}
                  | progressPercentage: {progressPercentage.toFixed(1)}%
                </Typography>
              </Box>

              {/* Progress Line Positioning Debug */}
              <Box
                sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}
              >
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                    fontWeight: 'bold',
                    display: 'block',
                    mb: 0.5,
                  })}
                >
                  <span role="img" aria-label="Tools">
                    🔧
                  </span>{' '}
                  Line Positioning Debug:
                </Typography>
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    fontSize: '0.7rem',
                  })}
                >
                  Background Line: left: {(50 / steps.length).toFixed(2)}%,
                  right: {(50 / steps.length).toFixed(2)}%
                </Typography>
                <br />
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    fontSize: '0.7rem',
                  })}
                >
                  Current Line: left:{' '}
                  {(
                    (activeStepIndex * 100) / steps.length +
                    50 / steps.length
                  ).toFixed(2)}
                  %
                </Typography>
                <br />
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    fontSize: '0.7rem',
                  })}
                >
                  Current Width:{' '}
                  {(
                    (progressPercentage / 100) *
                    ((100 - 100 / steps.length) / 100)
                  ).toFixed(2)}
                  %
                </Typography>
              </Box>

              {/* Visual progress bar representation */}
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                    fontSize: '0.7rem',
                  })}
                >
                  Visual Representation:
                </Typography>
                <Box
                  sx={(theme) => ({
                    mt: 0.5,
                    height: 6,
                    bgcolor: theme.palette.action.disabled,
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`,
                  })}
                >
                  {/* Completed segments */}
                  {activeStepIndex > 0 && (
                    <Box
                      sx={(theme) => ({
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${(activeStepIndex / (steps.length - 1)) * 100}%`,
                        bgcolor: theme.palette.success.main,
                        borderRadius: 2,
                      })}
                    />
                  )}
                  {/* Current progress */}
                  {progressPercentage > 0 && (
                    <Box
                      sx={(theme) => ({
                        position: 'absolute',
                        left: `${(activeStepIndex / (steps.length - 1)) * 100}%`,
                        top: 0,
                        height: '100%',
                        width: `${(progressPercentage / 100) * (100 / (steps.length - 1))}%`,
                        bgcolor: theme.palette.warning.main,
                        borderRadius: 2,
                      })}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Column */}
        <Box>
          {/* Active Step Details */}
          {activeStepIndex >= 0 && (
            <Box
              sx={(theme) => ({
                mb: 2,
                p: 1.5,
                bgcolor: theme.palette.action.selected,
                borderRadius: 2,
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: theme.shadows[1],
              })}
            >
              <Typography
                variant="caption"
                component="div"
                sx={(theme) => ({
                  fontWeight: theme.typography.fontWeightBold,
                  color: theme.palette.primary.main,
                  mb: 0.5,
                  fontSize: '0.85rem',
                })}
              >
                <span role="img" aria-label="Target">
                  🎯
                </span>{' '}
                Active Step Analysis:
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                })}
              >
                Step Name: {steps[activeStepIndex]?.name || 'N/A'}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                })}
              >
                Step Index: {activeStepIndex} (of {steps.length - 1} segments)
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                })}
              >
                Base: ${steps[activeStepIndex].buyingPower.toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                })}
              >
                Target: ${steps[activeStepIndex].target.toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                })}
              >
                Current PNL: ${debug.actualPNL.toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                })}
              >
                Current Equity: $
                {(
                  steps[activeStepIndex].buyingPower + debug.actualPNL
                ).toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                })}
              >
                Target Equity: $
                {(
                  steps[activeStepIndex].buyingPower +
                  steps[activeStepIndex].target
                ).toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                })}
              >
                Progress in Step:{' '}
                {(
                  (debug.actualPNL / steps[activeStepIndex].target) *
                  100
                ).toFixed(1)}
                %
              </Typography>
            </Box>
          )}

          {/* Step Details */}
          <Box>
            <Typography variant="caption" component="div" sx={{ mb: 1 }}>
              <strong>Step Details:</strong>
            </Typography>
            <Box
              sx={(theme) => ({
                maxHeight: theme.spacing(50),
                overflowY: 'auto',
                pr: 1,
              })}
            >
              {steps.map((step, index) => (
                <Typography
                  key={step._id}
                  variant="caption"
                  component="div"
                  onClick={() => onDebugStepClick?.(step._id)}
                  sx={(theme) => ({
                    cursor: onDebugStepClick ? 'pointer' : 'default',
                    padding: onDebugStepClick ? '4px 8px' : '2px 0',
                    marginBottom: '2px',
                    borderRadius: 1,
                    backgroundColor: step.active
                      ? theme.palette.primary.light
                      : 'transparent',
                    color: step.active
                      ? theme.palette.primary.contrastText
                      : 'inherit',
                    '&:hover': onDebugStepClick
                      ? {
                          backgroundColor: step.active
                            ? theme.palette.primary.main
                            : 'action.hover',
                        }
                      : {},
                    transition: 'all 0.2s ease-in-out',
                  })}
                >
                  {index + 1}. {step.name} (ID: {step._id}) - {step.category}
                  {step.active && ' 👈 ACTIVE'}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
